import { primaryActionStyle } from '../primaryActionStyle';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BLUE, PINKL } from './palette';
import { WORKOUT_GROUP_BY_OPTIONS, WORKOUT_SUMMARY_TYPE_OPTIONS } from './pmcSettings';
import { SEG_PILL, SEG_PILL_ACTIVE, SEG_TRACK } from './segmentControlStyles';

type VariantChartType = 'periodic_chart' | 'time_in_zone' | 'power_profile';

interface ChartVariantSettingsPanelProps {
  open: boolean;
  chartType: VariantChartType;
  setting: Record<string, unknown>;
  chartTitle: string;
  saving?: boolean;
  onClose: () => void;
  onSave: (setting: Record<string, unknown>) => void;
}

const ZONE_TYPE_OPTIONS = [
  { value: 'time_in_zone_hr', labelKey: 'charts.defs.zones.time_in_zone_hr.eye' },
  { value: 'time_in_zone_pw', labelKey: 'charts.defs.zones.time_in_zone_pw.eye' },
  { value: 'time_in_zone_sd', labelKey: 'charts.defs.zones.time_in_zone_sd.eye' },
  { value: 'time_in_zone_cd', labelKey: 'charts.defs.zones.time_in_zone_cd.eye' },
];

function SegmentField({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
}) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={FIELD_LABEL}>{label}</div>
      <div style={{ ...SEG_TRACK, flexWrap: 'wrap' }}>
        {options.map((option) => {
          const active = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onChange(option.value)}
              style={{ ...SEG_PILL, ...(active ? SEG_PILL_ACTIVE : {}) }}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function ChartVariantSettingsPanel({
  open,
  chartType,
  setting,
  chartTitle,
  saving,
  onClose,
  onSave,
}: ChartVariantSettingsPanelProps) {
  const { t } = useTranslation();
  const [groupCount, setGroupCount] = useState('1');
  const [metric, setMetric] = useState('tss_actual');
  const [groupBy, setGroupBy] = useState('week');
  const [zoneType, setZoneType] = useState('time_in_zone_hr');

  useEffect(() => {
    if (!open) return;
    setGroupCount(String(setting.group_count ?? 1));
    const summary = setting.workout_summery as Record<string, unknown> | undefined;
    setMetric(String(summary?.workout_summery_types ?? 'tss_actual'));
    setGroupBy(String(summary?.group_by ?? 'week'));
    setZoneType(String(setting.zone_type ?? 'time_in_zone_hr'));
  }, [open, setting]);

  const metricOptions = useMemo(
    () => WORKOUT_SUMMARY_TYPE_OPTIONS.map((option) => ({
      value: option.value,
      label: t(`charts.pmcSettings.summaryMetrics.${option.value}`, { defaultValue: option.label }),
    })),
    [t],
  );
  const groupOptions = useMemo(
    () => WORKOUT_GROUP_BY_OPTIONS.map((option) => ({
      value: option.value,
      label: t(`charts.pmcSettings.groupBy.${option.value}`, { defaultValue: option.label }),
    })),
    [t],
  );
  const zoneOptions = useMemo(
    () => ZONE_TYPE_OPTIONS.map((option) => ({
      value: option.value,
      label: t(option.labelKey),
    })),
    [t],
  );

  if (!open) return null;

  const save = () => {
    if (chartType === 'power_profile') {
      onSave({ ...setting, group_count: Number(groupCount) });
    } else if (chartType === 'periodic_chart') {
      const currentSummary = setting.workout_summery as Record<string, unknown> | undefined;
      onSave({
        ...setting,
        workout_summery: {
          ...(currentSummary ?? {}),
          show_workout_summery: true,
          workout_summery_types: metric,
          group_by: groupBy,
        },
      });
      return;
    }
    onSave({ ...setting, zone_type: zoneType });
  };

  return (
    <>
      <div onClick={onClose} style={BACKDROP_STYLE} />
      <div style={PANEL_STYLE}>
        <div style={HEADER_STYLE}>
          <div style={{ minWidth: 0 }}>
            <div style={TITLE_STYLE}>{t('charts.variantSettings.title')}</div>
            <div style={SUBTITLE_STYLE}>{chartTitle}</div>
          </div>
          <button type="button" onClick={onClose} style={CLOSE_BTN} aria-label={t('common.close')}>
            ×
          </button>
        </div>

        <div style={CONTENT_STYLE}>
          {chartType === 'power_profile' ? (
            <SegmentField label={t('charts.powerProfile.grouping')} value={groupCount} onChange={setGroupCount}
              options={[
                { value: '5200', label: t('charts.powerProfile.singleGroup') },
                { value: '1', label: t('charts.powerProfile.weekly') },
                { value: '4', label: t('charts.powerProfile.monthly') },
                ...(!['1', '4', '5200'].includes(groupCount) ? [{ value: groupCount, label: t('charts.powerProfile.weeks', { count: Number(groupCount) }) }] : []),
              ]} />
          ) : chartType === 'periodic_chart' ? (
            <>
              <SegmentField
                label={t('charts.variantSettings.metric')}
                value={metric}
                options={metricOptions}
                onChange={setMetric}
              />
              <SegmentField
                label={t('charts.variantSettings.period')}
                value={groupBy}
                options={groupOptions}
                onChange={setGroupBy}
              />
              <div style={PREVIEW_STYLE}>
                <span style={PREVIEW_LABEL_STYLE}>{t('charts.variantSettings.preview')}</span>
                <strong style={PREVIEW_VALUE_STYLE}>
                  {metricOptions.find((option) => option.value === metric)?.label ?? metric}
                  {' · '}
                  {groupOptions.find((option) => option.value === groupBy)?.label ?? groupBy}
                </strong>
              </div>
            </>
          ) : (
            <SegmentField
              label={t('charts.variantSettings.sensor')}
              value={zoneType}
              options={zoneOptions}
              onChange={setZoneType}
            />
          )}
        </div>

        <div style={FOOTER_STYLE}>
          <button type="button" onClick={onClose} style={CANCEL_BTN} disabled={saving}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            style={{ ...SAVE_BTN, opacity: saving ? 0.6 : 1 }}
          >
            {saving ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </div>
    </>
  );
}

const BACKDROP_STYLE: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 210,
  background: 'rgba(0,0,0,.4)',
};

const PANEL_STYLE: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  right: 0,
  bottom: 0,
  width: 400,
  maxWidth: '100vw',
  zIndex: 211,
  background: 'var(--surface-0)',
  boxShadow: '-8px 0 40px rgba(0,0,0,.45)',
  display: 'flex',
  flexDirection: 'column',
};

const HEADER_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
  padding: '24px 20px 16px',
  borderBottom: '1px solid var(--surface-sunken)',
};

const TITLE_STYLE: React.CSSProperties = {
  color: 'var(--fg-1)',
  fontSize: 18,
  fontWeight: 800,
};

const SUBTITLE_STYLE: React.CSSProperties = {
  color: 'var(--fg-4)',
  fontSize: 11,
  marginTop: 4,
};

const CLOSE_BTN: React.CSSProperties = {
  width: 30,
  height: 30,
  borderRadius: 9,
  border: 'none',
  cursor: 'pointer',
  background: 'var(--surface-sunken)',
  color: 'var(--fg-3)',
  fontSize: 18,
  flexShrink: 0,
};

const CONTENT_STYLE: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: '20px',
};

const FIELD_LABEL: React.CSSProperties = {
  color: 'var(--fg-3)',
  fontSize: 12,
  fontWeight: 700,
  marginBottom: 8,
};

const PREVIEW_STYLE: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  padding: 14,
  borderRadius: 14,
  background: 'var(--surface-sunken)',
  boxShadow: 'var(--nm-inset-sm)',
};

const PREVIEW_LABEL_STYLE: React.CSSProperties = {
  color: 'var(--fg-4)',
  fontSize: 10,
};

const PREVIEW_VALUE_STYLE: React.CSSProperties = {
  color: 'var(--fg-1)',
  fontSize: 14,
};

const FOOTER_STYLE: React.CSSProperties = {
  display: 'flex',
  gap: 10,
  padding: '16px 20px 20px',
  borderTop: '1px solid var(--surface-sunken)',
};

const CANCEL_BTN: React.CSSProperties = {
  flex: 1,
  padding: '10px 16px',
  borderRadius: 12,
  border: 'none',
  cursor: 'pointer',
  background: 'var(--surface-sunken)',
  color: 'var(--fg-2)',
  fontWeight: 700,
  fontSize: 12,
};

const SAVE_BTN: React.CSSProperties = {
  flex: 2,
  padding: '10px 16px',
  borderRadius: 12,
  border: 'none',
  cursor: 'pointer',
  ...primaryActionStyle,
  fontWeight: 700,
  fontSize: 12,
  boxShadow: primaryActionStyle.boxShadow,
};
